<template>
  <section id="summary" class="pt-2 pb-0" v-if="!isLoading && summary">
    <div id="credit-explainer" class="px-3">
      <h5 class="py-0 text-center" id="approved-amount">
        {{ t("JOINT_LOAN_NOTIFICATION.YOU_BELIEVED_IN_US") }}
        <br />
        <b>{{ t("JOINT_LOAN_NOTIFICATION.YOU_UNLOCKED_A_CL") }}</b>
        <br />
        <span class="text-center fs-1">{{
          formatCurrency(summary.availableCredit)
        }}</span>
      </h5>
    </div>
    <img
      :src="useAsset('img/credit-line/coins-2.svg')"
      alt="coins"
      class="position-absolute top-0 z-3 coins-img"
    />
  </section>
  <section class="mt-4 w-100 items-header">
    <h5>{{ t("JOINT_LOAN_NOTIFICATION.THE_PAYJOY_YOU_KNOW") }}</h5>
  </section>
  <section class="mt-4 w-100 px-3">
    <div class="d-flex mt-4 flex-fill" v-for="i in 4" :key="i">
      <div class="ms-0 me-3">
        <img
          alt="checkmark"
          :src="`${useAsset('img/credit-line/checkmark.png')}`"
        />
      </div>
      <div
        class="d-flex align-items-center check-legend"
        v-html="t(`JOINT_LOAN_LINE_WELCOME_ITEMS.${i}`)"
      ></div>
    </div>
  </section>

  <section
    class="w-100 mx-auto flex-fill mt-5 pt-3 px-3 position-fixed bottom-0"
  >
    <Button class="go-home-btn mb-3" @click="goToCard">
      {{ t("JOINT_LOAN_NOTIFICATION.GET_MY_CARD") }}
    </Button>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import Button from "@/apps/credit-line/components/Button.vue";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";

declare const Android: AndroidInterface;

const { t } = useI18n();
const router = useRouter();
const { summary, isLoading, fetchSummary } = useCreditLineSummary();

onMounted(async () => {
  try {
    await fetchSummary();
    Android.finishedLoading();
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CreditLineLimitValuePropPopUp,
      "PACMAN_ONBOARDING"
    );
  } catch (error) {
    router.replace({
      name: "CreditLineErrorView",
      query: { title: error?.message },
    });
  }
});

const formatCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat("en-US");
  return "$" + formatter.format(amount);
};

const goToCard = () => {
  router.replace({ name: "CreditCardShippingAddressView" });
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

main {
  position: relative;
  & * {
    position: relative;
  }
}

.light-text {
  color: $pj_color-cool-gray;
}

.go-home-btn {
  border-radius: 5px;
  background: #007c3b;
  height: 48px;
  padding: 10px 24px;
  color: white;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  width: 100%;
  border: none;
}

label {
  cursor: pointer;
  color: $pj-color-green;
  font-weight: bold;
  font-size: medium;
}

#credit-explainer {
  position: relative;
  margin-top: 9.8rem;
}

.items-summary {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.items-summary .row {
  margin-top: 9.08rem;
  color: #666;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
}

#approved-amount {
  color: #fff;
  text-align: center;
  font-family: Inter, serif;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.1875rem;
  letter-spacing: 0.01125rem;

  & b {
    color: #fff;
    text-align: center;
    font-family: Inter, serif;
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.4375rem;
    letter-spacing: 0.01125rem;
  }

  & span {
    color: #c7fd17;
    font-family: Inter, serif;
    font-size: 2.25rem;
    font-style: normal;
    font-weight: 600;
    line-height: 3rem;
    letter-spacing: 0.01125rem;
  }
}

.check-legend {
  color: #666;
  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
}

#summary {
  background-color: #007c3b;
  width: 120%;
  height: 19.25rem;
  position: relative;
  padding: 1.44rem 1.38rem;
}

.items-header {
  h5 {
    color: #007c3b;
    font-family: Inter, serif;
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.75rem;
    letter-spacing: 0.01125rem;
  }
  margin-bottom: 2.19rem;
  padding-left: 1.125rem;
  padding-right: 1.125rem;
}

.coins-img {
  width: 100%;
  z-index: 1000;
  padding-left: 5%;
  padding-right: 5%;
  left: 0;
  right: 0;
  margin-top: 3.58rem;
}

.material-symbols-outlined {
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}

* {
  font-family: Inter, serif !important;
  max-width: 100vw !important;
}
</style>
